import { Box, Button, Flex, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { API_ROUTERS, USER_GENDER_OPTIONS } from '../../../../data';
import { useRequest } from '../../../../hooks';
import { useStoreContext } from '../../../../hooks/useStore';
import {
  EDIT_PROFILE_DEFAULT_VALUES,
  EDIT_PROFILE_VALIDATION,
  IEditProfileForm,
} from '../../../../utils/forms/EditProfile';
import { SubmitFormBtn } from '../../../Buttons/SubmitFormBtn';
import { FormInput } from '../../../Form/FormInput';
import { FormPhoneInput } from '../../../Form/FormPhoneInput';
import { FormSelect } from '../../../Form/FormSelect';
import { dateFormatForReq, formValidateByRes } from '../../../../services';
import { FormInputFile } from '../../../Form/FormInputFile';
import { FormDateInput } from '../../../Form/FormDataInput';
import { FormInputZip } from '../../../Form/FormInputZip';

export const ProfileTabEdit = ({ onChangeTab }: { onChangeTab: (x: number) => void }) => {
  const { t } = useTranslation();
  const { store, setUser } = useStoreContext();
  const { authRequest } = useRequest();
  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<IEditProfileForm>({
    mode: 'onChange',
    defaultValues: store?.user || EDIT_PROFILE_DEFAULT_VALUES,
    resolver: yupResolver(EDIT_PROFILE_VALIDATION),
  });

  const goBack = () => onChangeTab(0);

  const onSubmit = async (formValues: IEditProfileForm) => {
    const { birthday, code, avatar, ...restFormValues } = formValues;
    const sendData = { birthday: dateFormatForReq(birthday) , ...restFormValues, avatar: typeof avatar === 'string' ? null : avatar };

    try {
      const { data } = await authRequest({
        method: 'post',
        url: API_ROUTERS.VISITOR,
        data: sendData,
      });
      setUser(data);
      goBack();
    } catch (e) {
      formValidateByRes(e, setError);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
        <VStack mt={6} maxW='xl' spacing={4} mb={2}>
          <FormInput
            control={control}
            name='firstName'
            label={`${t('input.name.label')}`}
            placeholder={`${t('input.name.placeholder')}`}
          />
          <FormInput
            control={control}
            name='lastName'
            label={`${t('input.last.name.label')}`}
            placeholder={`${t('input.last.name.placeholder')}`}
          />

          <FormDateInput 
            control={control} 
            name='birthday'
            label={`${t('input.birth.date.label')}`} 
          />

          <FormInputFile
            control={control}
            name='avatar'
            label={`${t('input.profile.image.label')}`}
            accept='.png, .jpeg, .gif'
            helperText={`${t('input.profile.image.helper')}`}
          />

          <FormInput
            control={control}
            name='email'
            label={`${t('input.email.label')}`}
            placeholder={`${t('input.email.label')}`}
            type='email'
            isReadOnly
          />

          <FormInputZip
            control={control}
            placeholder={`${t('input.postal.code.placeholder')}`}
            name='postalCode'
            label={`${t('input.postal.code.label')}`}
            maxLength={7}
          />

          <FormPhoneInput
            control={control}
            placeholder='xxxxxxxxx'
            name='mobilePhone'
            label={`${t('input.mobile.phone.label')}`}
          />

          <FormSelect
            control={control}
            name='gender'
            label={`${t('input.gender.label')}`}
            placeholder={`${t('input.gender.placeholder')}`}
            options={USER_GENDER_OPTIONS}
          />

          <Box w='100%'>
            <Flex w='100%' justifyContent='space-between' alignItems='center' mt={6}>
              <SubmitFormBtn type='submit' minH={'58px'} isDisabled={!isValid}>
                {t('save.changes')}
              </SubmitFormBtn>

              <Button variant='linkUnderlinePrimary' size='sm' onClick={goBack}>
                {t('cancel')}
              </Button>
            </Flex>
          </Box>
        </VStack>
      </form>
    </>
  );
};
